import { type DropDownResult, Notification } from '@myosh/odin-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useMatch, useNavigate, useParams } from 'react-router';
import type { ModuleForm } from '../../../@types/modules';
import Meta from '../../../features/meta/meta';
import useFetchAllFormsPermissions from '../../../hooks/use-fetch-all-forms-permissions';
import useFormPermissions from '../../../hooks/use-form-permissions';
import useProfileData from '../../../hooks/use-profile-data';
import DynamicPage from '../../../pages/dynamic.page';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useFormSettingsQuery } from '../../../redux/services/api';
import { useModulesQuery } from '../../../redux/services/module';
import { dynamicPageState, setSelectedForm } from '../../../redux/slices/dynamic-page-slice';
import { allFormsForm } from '../../../utils/data-transformation-utils';
import { extractFormPermissions } from '../../../utils/form-permissions-util';
import DynamicPageHeader from '../../dynamic-page/dynamic-page-header';
import DynamicPageLoader from '../../dynamic-page/dynamic-page-loader.component';
import RecordNavigatorRoute from '../../dynamic-page/record-navigator-route.component';
import useLegacyRecordUrl from '../../dynamic-page/use-legacy-record-url';

function DynamicPageRoute() {
  const { lastUsedForms } = useAppSelector(dynamicPageState);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { viewId, formId, schema } = useParams();
  const { pathname, state } = useLocation();
  const navigate = useNavigate();

  const [viewHasChanged, setViewHasChanged] = useState(false);
  const [calendarViewByModule, setCalendarViewByModule] = useState<Record<string, boolean>>({});

  // Get calendar view state for current module (defaults to false for new modules)
  const currentModuleId = viewId || '';

  const resetCalendarView = useCallback(() => {
    setCalendarViewByModule((prev) => ({
      ...prev,
      [currentModuleId]: false,
    }));
  }, [currentModuleId]);

  // Reset calendar view to grid when switching modules via side navigation
  useEffect(() => {
    if (viewId && currentModuleId) {
      resetCalendarView();
    }
  }, [viewId, currentModuleId, resetCalendarView]);

  // needed to resolve recordId when a url is copy/pasted
  const match = useMatch('/:schema/modules/:viewId/forms/:formId/records/:id');
  const { id: recordId } = match?.params ?? {};

  const { profileData: { user: userData } = {} } = useProfileData();
  const isLegacyUrl = formId === 'records';
  const { formId: legacyFormId, recordId: legacyRecordId, error: legacyRecordFetchError } = useLegacyRecordUrl();

  const lastUsedForm = useMemo(() => {
    if (viewId) {
      return lastUsedForms[viewId];
    }
    // this memo intentionally does not specify `lastUsedForms` as a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewId]);

  const { data: modules, isFetching: isModuleFormsFetching } = useModulesQuery({ includeFormList: true });

  const module = useMemo(() => {
    if (modules && viewId) {
      return modules.find((item) => item.viewId === viewId);
    }
  }, [modules, viewId]);

  const forms: Array<ModuleForm> = useMemo(() => {
    if (module?.forms) {
      const forms = [...module.forms];
      if (forms.length > 1 && !module.disableAllForms) {
        forms.unshift(allFormsForm);
      }
      return forms;
    }
    return [];
  }, [module]);

  const allFormIdsForActiveModule = useMemo(() => {
    return forms.map((form) => form.id).filter((id) => id > 0);
  }, [forms]);

  const { canExportRecord, listOfFormIdsWithCreatePermission } = useFetchAllFormsPermissions(
    allFormIdsForActiveModule,
    pathname.includes('/all-forms')
  );

  const showAllFormsFirst = useMemo(() => Boolean(module?.showAllFormsFirst), [module]);

  const resolvedForm: DropDownResult | undefined = useMemo(() => {
    if (forms && !isModuleFormsFetching) {
      // needed for backward URL compatiblity
      let _formId: string | number | undefined;
      if (isLegacyUrl && legacyFormId) {
        _formId = legacyFormId;
      } else if (!isLegacyUrl && formId) {
        _formId = formId;
      }

      const form = forms.find(
        (form: ModuleForm) => form.id === Number(_formId) || (form.id === 0 && _formId === 'all-forms')
      );

      if (_formId && form) {
        return {
          value: form.id,
          text: form.caption,
        };
      }

      const hasActiveForms = lastUsedForm !== undefined || forms.length > 0;
      if (hasActiveForms && (!isLegacyUrl || (isLegacyUrl && legacyRecordFetchError))) {
        let formToLoad = {} as DropDownResult;
        if (lastUsedForm) {
          formToLoad = lastUsedForm;
        } else if (showAllFormsFirst && forms.length > 1) {
          formToLoad = {
            value: 0,
            text: 'All forms',
          };
        } else if (forms) {
          const defaultForm = forms.find((form) => form.defaultForm === true);
          const isAllForms = pathname.includes('/all-forms');
          const moduleAllForms = forms.find((form) => form.id === 0);

          if (isAllForms && moduleAllForms) {
            formToLoad = {
              value: moduleAllForms.id,
              text: moduleAllForms.caption,
            };
          } else if (defaultForm) {
            formToLoad = {
              value: defaultForm.id,
              text: defaultForm.caption,
            };
          } else if (forms.length > 0) {
            formToLoad = {
              value: forms[0].id,
              text: forms[0].caption,
            };
          }
        }

        return formToLoad;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formId,
    legacyFormId,
    legacyRecordFetchError,
    isLegacyUrl,
    lastUsedForm,
    forms,
    showAllFormsFirst,
    isModuleFormsFetching,
  ]);

  //TODO: add allForms permission when we have the permission for that
  const isAllForms = resolvedForm?.value === 0;

  // Get form settings for calendar view logic
  const { data: formSettings } = useFormSettingsQuery(resolvedForm?.value as number, {
    skip: !resolvedForm?.value || resolvedForm.value === 0 || isAllForms,
  });

  const shouldAllowCalendarView = !isAllForms && formSettings?.enableCalendarView;

  // Get current calendar view state and respect enableCalendarView setting
  const isCalendarView = (calendarViewByModule[currentModuleId] ?? false) && shouldAllowCalendarView;

  // Force users out of calendar view when it becomes disabled
  useEffect(() => {
    if (calendarViewByModule[currentModuleId] && !shouldAllowCalendarView) {
      resetCalendarView();
    }
  }, [shouldAllowCalendarView, currentModuleId, calendarViewByModule, resetCalendarView]);

  const onFormChange = useCallback(
    (form: DropDownResult) => {
      // Reset calendar view to grid when switching forms
      resetCalendarView();

      let path = pathname,
        state;
      if (isLegacyUrl) {
        path = pathname.substring(0, pathname.indexOf('/records'));
        state = { legacyRecordId }; // store legacyRecordId in state so it can be resolved after navigate
      } else if (pathname.includes('/forms')) {
        path = pathname.substring(0, pathname.indexOf('/forms'));
        state = { recordId };
      } else if (pathname.includes('/all-forms')) {
        path = pathname.substring(0, pathname.indexOf('/all-forms'));
        state = { recordId };
      }

      if (form.value === 0) {
        navigate(`${path}/all-forms`, { state });
      } else {
        navigate(`${path}/forms/${form.value}`, { state });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname, isLegacyUrl, legacyRecordId]
  );

  const replaceWithBasePath = useCallback(() => {
    if (resolvedForm) {
      onFormChange(resolvedForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedForm]);

  useEffect(() => {
    if (viewId && resolvedForm) {
      if (!pathname.includes(`/forms/${resolvedForm.value}`)) {
        // update URL when we resolve the form
        onFormChange(resolvedForm);

        if (!lastUsedForms[viewId]) {
          // update last used form if there was no value
          dispatch(setSelectedForm({ viewId, form: resolvedForm }));
        }
      } else if (recordId) {
        //otherwise, if we have a recordid, the we need to manually update last used form
        // only if it's different from the last used form for this view
        if (resolvedForm.value !== lastUsedForms[viewId]?.value) {
          dispatch(setSelectedForm({ viewId, form: resolvedForm }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewId, resolvedForm, pathname, onFormChange]);

  const { formPermissions, isLoading: areFormPermissionsLoading } = useFormPermissions(
    resolvedForm ? (resolvedForm?.value as number) : undefined
  );

  const recordsPermissions = !isAllForms
    ? extractFormPermissions(formPermissions)
    : { create: !userData?.superUser, showCreateButton: !userData?.superUser };

  const resolvedRecordId =
    recordId ??
    legacyRecordId ??
    (state?.legacyRecordId ? String(state.legacyRecordId) : undefined) ??
    (state?.recordId ? String(state.recordId) : undefined);

  const shouldShowFormsView = viewId && resolvedForm && forms && module;
  const shouldShowNoFormsError = !resolvedForm && forms.length === 0 && !isModuleFormsFetching && module;
  const shouldHandleRecordNavigation = !areFormPermissionsLoading && formPermissions && resolvedRecordId;

  const toggleCalendarView = () => {
    if (shouldAllowCalendarView) {
      setCalendarViewByModule((prev) => ({
        ...prev,
        [currentModuleId]: !isCalendarView,
      }));
    }
  };

  if (shouldShowFormsView) {
    return (
      <>
        <Meta title={`myosh - ${resolvedForm?.text ?? t('records')}`} />
        <div className="flex h-full w-full flex-col">
          <DynamicPageHeader
            selectedForm={resolvedForm}
            module={module}
            forms={forms}
            onFormChange={onFormChange}
            permissions={recordsPermissions}
            viewHasChanged={viewHasChanged}
            formPermissions={formPermissions}
            allFormsExportPermission={canExportRecord}
            listOfFormIdsWithCreatePermission={listOfFormIdsWithCreatePermission}
            onToggleCalendarView={toggleCalendarView}
            isCalendarView={isCalendarView}
          />
          <DynamicPage
            key={`module_${viewId}_forms_${resolvedForm.value}`}
            selectedForm={resolvedForm}
            module={module}
            onViewChange={setViewHasChanged}
            isCalendarView={isCalendarView}
          />
        </div>
        {/* Handles the case when the user copy/paste's a record URL */}
        {shouldHandleRecordNavigation && (
          <RecordNavigatorRoute
            id={resolvedRecordId}
            schema={schema}
            form={resolvedForm}
            module={module}
            formPermissions={formPermissions}
            replaceWithBasePath={replaceWithBasePath}
          />
        )}
      </>
    );
  } else if (shouldShowNoFormsError) {
    return (
      <div className="flex w-full justify-center pt-16">
        <Notification key="errorNotification" description={t('no-forms')} type="error" />
      </div>
    );
  }

  return <DynamicPageLoader />;
}

export default DynamicPageRoute;
